import 'semantic-styles-ccs/dist/index.css';
import './assets/styles/toast.css';

import { lazy, Suspense, useEffect, useState } from 'react';

import { Outlet, RouteObject, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { Modal } from 'modal-react-ccs';
import { ToastContainer } from 'react-toastify';

import LazyLoader from './components/LazyLoader';
import LazyApplication from './components/LazyApplication';
import NotFound from './components/NotFound';
import I18n from './I18n';

import { PlaceholderWindow } from './components/PlaceholderWindow';

import { AppProvider } from './providers/AppProvider';

import { useApp, useRest } from './hooks';

import pkg from '../package.json';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

import Home from './pages/Home';
import { PageWrapper } from './components/PageWrapper';

import { CustomObject, Route } from './types';

import routes from './routes';

const Core = lazy(() => import('./components/Core'));
const Login = lazy(() => import('./pages/Login'));

enum APP_STAGE {
    LOGIN = 0,
    HOME = 1,
    SERVICE_WORK,
    BOOTSTRAPING,
}

const App = (): JSX.Element => {
    const [stage, setStage] = useState<APP_STAGE>(APP_STAGE.SERVICE_WORK);
    const { token, loginError } = useApp();
    const { consume } = useRest();

    const onServiceWorkerUpdate = () => {
        if (stage === APP_STAGE.SERVICE_WORK) {
            window.location.reload();
        }
    };

    const onServiceWorkerSuccess = () => {
        if (stage === APP_STAGE.SERVICE_WORK) {
            setStage(APP_STAGE.BOOTSTRAPING);
        }
    };

    const onServiceWorkerActivated = async () => {
        try {
            const response = await fetch('/manifest.json');
            const manifest = await response.json();

            if (pkg.version === manifest.version) {
                setStage(APP_STAGE.BOOTSTRAPING);
            } else {
                window.location.reload();
            }
        } catch (error) {
            console.error(error);

            setStage(APP_STAGE.BOOTSTRAPING);
        }
    };

    const checkAppVersion = async () => {
        // Possuí service worker registrado
        const _window = window as serviceWorkerRegistration.Window;
        if (_window.sw) {
            return _window.sw.update().catch((error: CustomObject) => {
                console.error(error);

                setStage(APP_STAGE.BOOTSTRAPING);
            });
        }

        setStage(APP_STAGE.BOOTSTRAPING);
    };

    const onServiceWorkerFailure = () => {
        setStage(APP_STAGE.BOOTSTRAPING);
    };

    const fireServiceWorker = async () => {
        try {
            await serviceWorkerRegistration.register({
                onUpdate: onServiceWorkerUpdate,
                onSuccess: onServiceWorkerSuccess,
                onActivated: onServiceWorkerActivated,
            });

            await checkAppVersion();
        } catch (error) {
            console.error(error);

            onServiceWorkerFailure();
        }
    };

    useEffect(() => {
        fireServiceWorker();
    }, []);

    useEffect(() => {
        if (stage !== APP_STAGE.SERVICE_WORK) {
            if (token) {
                (async () => {
                    try {
                        const requestValidToken = await consume<CustomObject>('get', '/sessions/checkValidToken');

                        const isValidToken = requestValidToken;

                        if (isValidToken.status == true) {
                            setStage(APP_STAGE.HOME);
                        } else {
                            setStage(APP_STAGE.LOGIN);
                        }
                    } catch (error) {
                        console.error(error);
                        setStage(APP_STAGE.LOGIN);
                    }
                })();
            } else {
                setStage(APP_STAGE.LOGIN);
            }
        }
    }, [token, stage]);

    useEffect(() => {
        if (loginError) {
            setStage(APP_STAGE.LOGIN);
            return;
        }
    }, [loginError]);

    const getPagePlaceholder = (placeholder?: 'form' | 'table' | (() => JSX.Element)) => {
        if (typeof placeholder === 'function') {
            return placeholder();
        }

        return <PlaceholderWindow type={placeholder || 'form'} />;
    };

    const mountRouteObject = (route: Route): RouteObject => ({
        path: route.path,
        handle: {
            crumb: {
                title: route.title,
                path: route.path,
            },
        },
        element: <Outlet />,
        children: [
            {
                index: true,
                element: (
                    <Suspense fallback={getPagePlaceholder(route.placeholder)}>
                        <PageWrapper Component={route.element} />
                    </Suspense>
                ),
            },
            ...(route.children?.map(_route2 => mountRouteObject(_route2)) || []),
        ],
    });

    const getRender = () => {
        let toRender;
        let _routes: RouteObject[] = [];

        switch (stage) {
            case APP_STAGE.LOGIN:
                toRender = <Login error={loginError} />;
                break;
            case APP_STAGE.HOME:
                _routes = [
                    {
                        path: '/',
                        handle: {
                            crumb: {
                                title: 'Home',
                            },
                        },
                        errorElement: (
                            <Core>
                                <NotFound buttonText='Go back to Home' route='/' />
                            </Core>
                        ),
                        element: <Core />,
                        children: [
                            {
                                index: true,
                                element: (
                                    <Suspense fallback={<LazyLoader />}>
                                        <Home />
                                    </Suspense>
                                ),
                            },
                            ...routes.map(route => mountRouteObject(route)),
                        ],
                    },
                ];

                toRender = <RouterProvider router={createBrowserRouter(_routes)} />;
                break;
            case APP_STAGE.SERVICE_WORK:
                toRender = <LazyApplication />;
                break;
            default:
                toRender = null;
        }

        return <Suspense fallback={<LazyApplication />}>{toRender}</Suspense>;
    };

    return getRender();
};

const Wrapper: React.FunctionComponent = () => {
    return (
        <>
            <ToastContainer
                className={'toast'}
                position='top-right'
                autoClose={5000}
                hideProgressBar={false}
                limit={8}
                newestOnTop
                closeOnClick={false}
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <I18n>
                <AppProvider>
                    <Modal />
                    <App />
                </AppProvider>
            </I18n>
        </>
    );
};

export default Wrapper;
